"use client";

import ParallaxSpaceBg from "@/components/about/parallax-space-bg";
import { PiShootingStars } from "@/components/about/pi-shooting-stars";
import { Footer } from "@/components/layout/footer";
import { Header } from "@/components/layout/header";
import Link from "next/link";

function LogoCard({
  bgClass,
  logoSrc,
  alt,
  downloadHref,
  downloadName,
  logoClassName,
}: {
  bgClass: string;
  logoSrc: string;
  alt: string;
  downloadHref: string;
  downloadName: string;
  logoClassName?: string;
}) {
  return (
    <a
      href={downloadHref}
      download={downloadName}
      aria-label={`Download ${downloadName}`}
      className={`rounded-2xl p-10 md:p-12 flex items-center justify-center min-h-[240px] md:min-h-[280px] transition-transform hover:scale-[1.01] cursor-pointer ${bgClass}`}
    >
      <img
        src={logoSrc}
        alt={alt}
        className={`${logoClassName ?? "h-14 md:h-[4.5rem]"} w-auto max-w-full object-contain`}
      />
    </a>
  );
}

export default function BrandPage() {
  return (
    <main className="relative min-h-screen flex flex-col">
      <ParallaxSpaceBg>
        <PiShootingStars />
      </ParallaxSpaceBg>
      <div className="relative z-10">
        <Header />

        <div className="flex-1 py-16 md:py-24">
          <div className="container mx-auto max-w-[1024px] px-3 sm:px-4">
            <div className="flex justify-center mb-8">
              <img
                src="/images/marketing/branding/hero.webp"
                alt="PlayMath brand"
                className="h-36 md:h-48 w-auto rounded-lg object-contain"
              />
            </div>
            <h1 className="charlie-kingdom-font text-3xl md:text-5xl font-extrabold text-white text-center">
              PlayMath Brand Guidelines
            </h1>
            <p className="mt-4 text-center text-white/90 max-w-2xl mx-auto">
              Resources and usage guidelines for educators and partners.
            </p>

            {/* Download Brand Assets */}
            <section className="mt-12 flex justify-center">
              <a
                href="/resources/PlayMath.org%20Brand%20Assets.zip"
                download="PlayMath.org Brand Assets.zip"
                className="inline-flex items-center gap-2 bg-cta-yellow text-black px-8 py-4 rounded-full charlie-kingdom-font text-lg font-bold hover:bg-cta-yellow-hover shadow-cta-yellow border-2 border-black transition-colors"
              >
                <span className="relative -top-0.5">Download brand assets</span>
              </a>
            </section>

            {/* Naming */}
            <section className="mt-16">
              <h2 className="text-2xl font-bold text-white mb-4">Naming</h2>
              <p className="text-white/90 leading-relaxed">
                “PlayMath.org” is a single name, spelled with a capital “P” and “M,” and the “.org”
                is always required. We ask that you refer to us as “PlayMath.org,” not “the PlayMath
                website.”
              </p>
            </section>

            {/* Usage */}
            <section className="mt-16">
              <h2 className="text-2xl font-bold text-white mb-4">Usage</h2>
              <p className="text-white/90 leading-relaxed">
                Leave ample space around PlayMath.org assets so they stay easy to recognize at a
                glance. They shouldn't feel cramped.
              </p>
              <div className="mt-4 rounded-xl border border-white/20 bg-black/20 p-4">
                <p className="text-white/90 leading-relaxed">
                  A friendly reminder: These graphics are proprietary and protected under
                  intellectual property laws. Please do not modify these files or present them in
                  ways that suggest sponsorship or endorsement by PlayMath.org. Do not use these
                  graphics in your own product or business name, and do not combine them with other
                  logos without written permission. Questions?{" "}
                  <Link href="/contact" className="text-cta-yellow hover:underline">
                    Get in touch
                  </Link>
                  .
                </p>
              </div>
            </section>

            {/* PlayMath logo (horizontal) - primary */}
            <section id="logos" className="mt-16 scroll-mt-8">
              <h2 className="text-2xl font-bold text-white mb-2">PlayMath.org wordmark</h2>
              <p className="text-white/80 text-sm mb-6">
                This is the primary PlayMath.org wordmark and should be used in most references to
                the brand when space allows. Choose the version that gives you the clearest contrast
                on your background, and avoid substituting other colors or treatments.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <LogoCard
                  bgClass="bg-white"
                  logoSrc="/images/logos/logo-black.svg"
                  alt="PlayMath.org logo - for light backgrounds"
                  downloadHref="/images/logos/logo-black.svg"
                  downloadName="playmath-logo-black.svg"
                />
                <LogoCard
                  bgClass="bg-ink"
                  logoSrc="/images/logos/logo-white.svg"
                  alt="PlayMath.org logo - for dark backgrounds"
                  downloadHref="/images/logos/logo-white.svg"
                  downloadName="playmath-logo-white.svg"
                />
              </div>
            </section>

            {/* PlayMath logo (stacked) */}
            <section className="mt-16">
              <h2 className="text-2xl font-bold text-white mb-2">PlayMath.org wordmark, stacked</h2>
              <p className="text-white/80 text-sm mb-6">
                The stacked wordmark works best when horizontal space is limited, like square
                thumbnails, narrow layouts, or logo grids. Use it when the full horizontal mark
                feels cramped, while keeping the standard wordmark as the default where possible.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <LogoCard
                  bgClass="bg-white"
                  logoSrc="/images/logos/logo-stacked-black.svg"
                  alt="PlayMath.org stacked logo - for light backgrounds"
                  downloadHref="/images/logos/logo-stacked-black.svg"
                  downloadName="playmath-logo-stacked-black.svg"
                  logoClassName="h-28 md:h-32"
                />
                <LogoCard
                  bgClass="bg-ink"
                  logoSrc="/images/logos/logo-stacked-white.svg"
                  alt="PlayMath.org stacked logo - for dark backgrounds"
                  downloadHref="/images/logos/logo-stacked-white.svg"
                  downloadName="playmath-logo-stacked-white.svg"
                  logoClassName="h-28 md:h-32"
                />
              </div>
            </section>

            {/* Partnership & press */}
            <section className="mt-16">
              <h2 className="text-2xl font-bold text-white mb-4">Partnership & press</h2>
              <p className="text-white/90 mb-4">
                Partnership, press, or brand questions? Get in touch.
              </p>
              <Link
                href="/contact"
                className="inline-block bg-cta-yellow text-black px-8 py-4 rounded-full charlie-kingdom-font text-xl font-bold hover:bg-cta-yellow-hover shadow-cta-yellow border-4 border-black transition-colors"
              >
                <span className="relative -top-1">Get in touch</span>
              </Link>
            </section>
          </div>
        </div>

        <Footer />
      </div>
    </main>
  );
}
